import type { CryptoOnchainNetwork, CryptoOnchainSelectedAssets } from '@/lib/api/crypto-onchain';

import {
  CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS,
  isCryptoOnchainPayoutKindLive,
  type CryptoOnchainFormValues,
  type CryptoOnchainPayoutAddressKind,
} from './crypto-onchain-form.schema';

/**
 * The networks and coins the checkout offers, picked in the crypto form. One pick per network
 * (mainnets, or the testnets in sandbox mode): `{ [chainId]: [symbols] }`. A store that never picked
 * offers every network it has a wallet for; once it picks, a network or coin added later stays off
 * until ticked.
 */
export type CryptoOnchainAssetPick = Record<string, string[]>;

/** One row of the picker: a network of the mode, pickable once its wallet is filled in. */
export interface CryptoOnchainPickerRow {
  network: CryptoOnchainNetwork;
  pickable: boolean;
}

/** What a save sends for the networks: the one pick that changed. */
export type CryptoOnchainSelectedAssetsInput =
  | { mainnet: CryptoOnchainAssetPick }
  | { testnet: CryptoOnchainAssetPick };

/** Kinds whose test networks pay a testnet wallet of their own (held apart, saved by their own card). */
const TESTNET_WALLET_KINDS = ['btc', 'ltc'] as const;

function isPayoutKind(kind: string): kind is CryptoOnchainPayoutAddressKind {
  return CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS.some((candidate) => candidate === kind);
}

/**
 * Whether the store has the wallet that receives a network: the form's live wallet, or for a Bitcoin
 * or Litecoin testnet its saved testnet wallet.
 */
export function hasWalletFor(
  network: CryptoOnchainNetwork,
  values: CryptoOnchainFormValues,
  testnetWallets: Partial<Record<string, string>>,
): boolean {
  if (network.testnet && TESTNET_WALLET_KINDS.some((kind) => kind === network.kind)) {
    return (testnetWallets[network.kind] ?? '').trim() !== '';
  }
  return (
    isPayoutKind(network.kind) &&
    isCryptoOnchainPayoutKindLive(network.kind) &&
    values[network.kind].trim() !== ''
  );
}

/** What the picker lists: the mode's networks the store has a wallet for, then the coming-soon ones. */
export function pickerRows(
  networks: CryptoOnchainNetwork[],
  isSandbox: boolean,
  values: CryptoOnchainFormValues,
  testnetWallets: Partial<Record<string, string>>,
): CryptoOnchainPickerRow[] {
  const ofMode = networks.filter((network) => network.testnet === isSandbox);
  return [
    ...ofMode
      .filter((network) => !network.comingSoon && hasWalletFor(network, values, testnetWallets))
      .map((network) => ({ network, pickable: true })),
    ...ofMode.filter((network) => network.comingSoon).map((network) => ({ network, pickable: false })),
  ];
}

/** The ticks the picker starts from: the saved pick of the mode, or every coin of every network offered. */
export function initialPicks(
  networks: CryptoOnchainNetwork[],
  isSandbox: boolean,
  saved: CryptoOnchainSelectedAssets | null | undefined,
): CryptoOnchainAssetPick {
  const pick = isSandbox ? saved?.testnet : saved?.mainnet;
  if (pick) return pick;
  return Object.fromEntries(
    networks
      .filter((network) => network.testnet === isSandbox && !network.comingSoon)
      .map((network) => [String(network.chainId), network.assets.map((asset) => asset.symbol)]),
  );
}

/** The ticks with one coin of a network turned on or off, in the network's coin order. */
export function toggleAsset(
  picks: CryptoOnchainAssetPick,
  network: CryptoOnchainNetwork,
  symbol: string,
  on: boolean,
): CryptoOnchainAssetPick {
  const current = picks[String(network.chainId)] ?? [];
  const next = network.assets
    .map((asset) => asset.symbol)
    .filter((candidate) => (candidate === symbol ? on : current.includes(candidate)));
  return { ...picks, [String(network.chainId)]: next };
}

/** The ticks with every coin of a network turned on or off. */
export function toggleNetwork(
  picks: CryptoOnchainAssetPick,
  network: CryptoOnchainNetwork,
  on: boolean,
): CryptoOnchainAssetPick {
  return { ...picks, [String(network.chainId)]: on ? network.assets.map((asset) => asset.symbol) : [] };
}

/** True when a network the store has a wallet for keeps at least one ticked coin. */
export function offersANetwork(rows: CryptoOnchainPickerRow[], picks: CryptoOnchainAssetPick): boolean {
  return rows.some((row) => row.pickable && (picks[String(row.network.chainId)]?.length ?? 0) > 0);
}

function canonical(picks: CryptoOnchainAssetPick): string {
  return JSON.stringify(
    Object.entries(picks)
      .filter(([, symbols]) => symbols.length > 0)
      .map(([chainId, symbols]) => [chainId, [...symbols].sort()] as const)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

export function picksChanged(picks: CryptoOnchainAssetPick, initial: CryptoOnchainAssetPick): boolean {
  return canonical(picks) !== canonical(initial);
}

/**
 * What a save sends for the networks: the mode's pick when the ticks changed (networks with no coin
 * left dropped), null otherwise, so an untouched picker never turns "every network" into a list.
 */
export function toSelectedAssetsInput(
  picks: CryptoOnchainAssetPick,
  initial: CryptoOnchainAssetPick,
  isSandbox: boolean,
): CryptoOnchainSelectedAssetsInput | null {
  if (!picksChanged(picks, initial)) return null;
  const pick = Object.fromEntries(Object.entries(picks).filter(([, symbols]) => symbols.length > 0));
  return isSandbox ? { testnet: pick } : { mainnet: pick };
}
